package no.vareflow.app.importer

/** One spreadsheet cell: its text as shown, and its number when it is numeric. */
data class SheetCell(val text: String, val number: Double? = null) {
    val isBlank: Boolean get() = text.isBlank() && number == null
    companion object { val EMPTY = SheetCell("") }
}

/** One sheet: rows of cells (row index 0 = spreadsheet row 1; missing cells are EMPTY). */
data class SheetData(val name: String, val rows: List<List<SheetCell>>) {
    fun cell(row: Int, col: Int): SheetCell = rows.getOrNull(row)?.getOrNull(col) ?: SheetCell.EMPTY
}

class SpreadsheetException(message: String) : IllegalArgumentException(message)

/** Minimal ZIP reader (stored + deflate), enough for .xlsx files. */
internal object ZipReader {
    private fun u16(b: ByteArray, i: Int) = (b[i].toInt() and 0xFF) or ((b[i + 1].toInt() and 0xFF) shl 8)
    private fun u32(b: ByteArray, i: Int) = u16(b, i) or (u16(b, i + 2) shl 16)

    fun isZip(data: ByteArray) = data.size > 4 && data[0] == 'P'.code.toByte() && data[1] == 'K'.code.toByte()

    /** Entry name → uncompressed bytes (read lazily by [read]). */
    fun entries(data: ByteArray): Map<String, () -> ByteArray> {
        var eocd = -1
        var i = data.size - 22
        val stop = maxOf(0, data.size - 22 - 65_557)
        while (i >= stop) {
            if (u32(data, i) == 0x06054b50) { eocd = i; break }
            i--
        }
        if (eocd < 0) throw SpreadsheetException("Not a ZIP file")
        val count = u16(data, eocd + 10)
        var p = u32(data, eocd + 16)
        val result = LinkedHashMap<String, () -> ByteArray>()
        repeat(count) {
            if (p + 46 > data.size || u32(data, p) != 0x02014b50) throw SpreadsheetException("Broken ZIP directory")
            val method = u16(data, p + 10)
            val compressed = u32(data, p + 20)
            val size = u32(data, p + 24)
            val nameLen = u16(data, p + 28)
            val extraLen = u16(data, p + 30)
            val commentLen = u16(data, p + 32)
            val local = u32(data, p + 42)
            val name = data.copyOfRange(p + 46, p + 46 + nameLen).decodeToString()
            result[name] = {
                val start = local + 30 + u16(data, local + 26) + u16(data, local + 28)
                when (method) {
                    0 -> data.copyOfRange(start, start + compressed)
                    8 -> Inflate.inflate(data, start, size)
                    else -> throw SpreadsheetException("Unsupported ZIP compression ($method)")
                }
            }
            p += 46 + nameLen + extraLen + commentLen
        }
        return result
    }
}

/** A tiny pull parser for the simple XML inside .xlsx files (no DTD, no CDATA needed). */
internal class XmlScanner(private val xml: String) {
    var pos = 0
        private set

    /** Next tag from [pos]: name (with "/" prefix for closing tags), attributes, self-closing; null at the end. */
    data class Tag(val name: String, val attrs: Map<String, String>, val selfClosing: Boolean, val start: Int, val end: Int)

    fun next(): Tag? {
        while (true) {
            val lt = xml.indexOf('<', pos)
            if (lt < 0) return null
            if (xml.startsWith("<?", lt) || xml.startsWith("<!", lt)) {
                val close = if (xml.startsWith("<!--", lt)) xml.indexOf("-->", lt).let { if (it < 0) xml.length else it + 3 } else xml.indexOf('>', lt) + 1
                pos = if (close <= 0) xml.length else close
                continue
            }
            val gt = xml.indexOf('>', lt)
            if (gt < 0) return null
            var body = xml.substring(lt + 1, gt)
            val selfClosing = body.endsWith("/")
            if (selfClosing) body = body.dropLast(1)
            val nameEnd = body.indexOfFirst { it == ' ' || it == '\t' || it == '\n' || it == '\r' }.let { if (it < 0) body.length else it }
            val name = body.substring(0, nameEnd).substringAfter(':').let { if (body.startsWith("/")) "/" + body.substring(1, nameEnd).substringAfter(':') else it }
            pos = gt + 1
            return Tag(name, parseAttrs(body.substring(nameEnd)), selfClosing, lt, gt + 1)
        }
    }

    /** Text from [pos] up to the next tag (entities decoded). */
    fun text(): String {
        val lt = xml.indexOf('<', pos).let { if (it < 0) xml.length else it }
        return unescape(xml.substring(pos, lt))
    }

    private fun parseAttrs(s: String): Map<String, String> {
        val map = HashMap<String, String>()
        var i = 0
        while (i < s.length) {
            while (i < s.length && s[i].isWhitespace()) i++
            val eq = s.indexOf('=', i)
            if (eq < 0) break
            val key = s.substring(i, eq).trim().substringAfter(':')
            var j = eq + 1
            while (j < s.length && s[j].isWhitespace()) j++
            if (j >= s.length) break
            val quote = s[j]
            val endQ = s.indexOf(quote, j + 1)
            if (endQ < 0) break
            map[key] = unescape(s.substring(j + 1, endQ))
            i = endQ + 1
        }
        return map
    }

    companion object {
        fun unescape(s: String): String {
            if ('&' !in s) return s
            val out = StringBuilder(s.length)
            var i = 0
            while (i < s.length) {
                val c = s[i]
                if (c == '&') {
                    val semi = s.indexOf(';', i)
                    if (semi > i) {
                        val entity = s.substring(i + 1, semi)
                        val decoded: String? = when {
                            entity == "amp" -> "&"
                            entity == "lt" -> "<"
                            entity == "gt" -> ">"
                            entity == "quot" -> "\""
                            entity == "apos" -> "'"
                            entity.startsWith("#x") -> entity.drop(2).toIntOrNull(16)?.let { codePointString(it) }
                            entity.startsWith("#") -> entity.drop(1).toIntOrNull()?.let { codePointString(it) }
                            else -> null
                        }
                        if (decoded != null) { out.append(decoded); i = semi + 1; continue }
                    }
                }
                out.append(c)
                i++
            }
            return out.toString()
        }

        private fun codePointString(cp: Int): String =
            if (cp < 0x10000) cp.toChar().toString()
            else {
                val v = cp - 0x10000
                charArrayOf((0xD800 + (v shr 10)).toChar(), (0xDC00 + (v and 0x3FF)).toChar()).concatToString()
            }
    }
}

/** Reads the sheets of an .xlsx file: cell texts and numbers (formulas give their saved result). */
object XlsxReader {
    fun read(data: ByteArray): List<SheetData> {
        val zip = ZipReader.entries(data)
        fun text(name: String): String? = zip[name]?.invoke()?.decodeToString()
        val workbook = text("xl/workbook.xml") ?: throw SpreadsheetException("Not an Excel workbook (.xlsx)")
        val rels = text("xl/_rels/workbook.xml.rels").orEmpty()
        val targets = HashMap<String, String>()
        XmlScanner(rels).let { s -> while (true) { val t = s.next() ?: break; if (t.name == "Relationship") targets[t.attrs["Id"].orEmpty()] = t.attrs["Target"].orEmpty() } }
        val shared = sharedStrings(text("xl/sharedStrings.xml"))
        val sheets = mutableListOf<SheetData>()
        val ws = XmlScanner(workbook)
        while (true) {
            val t = ws.next() ?: break
            if (t.name != "sheet") continue
            val name = t.attrs["name"].orEmpty()
            val target = targets[t.attrs["id"].orEmpty()] ?: continue
            val path = when {
                target.startsWith("/") -> target.drop(1)
                else -> "xl/" + target.removePrefix("./")
            }
            val xml = text(path) ?: continue
            sheets += SheetData(name, sheetRows(xml, shared))
        }
        if (sheets.isEmpty()) throw SpreadsheetException("The workbook has no sheets")
        return sheets
    }

    private fun sharedStrings(xml: String?): List<String> {
        if (xml == null) return emptyList()
        val list = mutableListOf<String>()
        val s = XmlScanner(xml)
        var current: StringBuilder? = null
        var inPhonetic = false
        while (true) {
            val t = s.next() ?: break
            when (t.name) {
                "si" -> if (t.selfClosing) list += "" else current = StringBuilder()
                "/si" -> { list += current?.toString().orEmpty(); current = null }
                "rPh" -> if (!t.selfClosing) inPhonetic = true
                "/rPh" -> inPhonetic = false
                "t" -> if (!t.selfClosing && !inPhonetic) current?.append(s.text())
            }
        }
        return list
    }

    /** "AB12" → column index 27. */
    internal fun columnIndex(ref: String): Int {
        var col = 0
        for (c in ref) {
            if (c !in 'A'..'Z') break
            col = col * 26 + (c - 'A' + 1)
        }
        return col - 1
    }

    private fun sheetRows(xml: String, shared: List<String>): List<List<SheetCell>> {
        val rows = HashMap<Int, HashMap<Int, SheetCell>>()
        val s = XmlScanner(xml)
        var rowIndex = -1
        var nextCol = 0
        var cellCol = -1
        var cellType = ""
        var value: String? = null
        var inline: StringBuilder? = null
        var inValue = false
        fun finishCell() {
            if (cellCol < 0) return
            val raw = value
            val cell = when (cellType) {
                "s" -> SheetCell(raw?.trim()?.toIntOrNull()?.let { shared.getOrNull(it) }.orEmpty())
                "inlineStr" -> SheetCell(inline?.toString().orEmpty())
                "str", "e" -> SheetCell(raw.orEmpty())
                "b" -> SheetCell(if (raw?.trim() == "1") "TRUE" else "FALSE")
                else -> raw?.trim()?.toDoubleOrNull()?.let { SheetCell(formatNumber(it), it) } ?: SheetCell(raw.orEmpty())
            }
            if (!cell.isBlank) rows.getOrPut(rowIndex) { HashMap<Int, SheetCell>() }[cellCol] = cell
            cellCol = -1; value = null; inline = null
        }
        while (true) {
            val t = s.next() ?: break
            when (t.name) {
                "row" -> {
                    rowIndex = t.attrs["r"]?.toIntOrNull()?.minus(1) ?: (rowIndex + 1)
                    nextCol = 0
                }
                "c" -> {
                    cellCol = t.attrs["r"]?.let { columnIndex(it) } ?: nextCol
                    nextCol = cellCol + 1
                    cellType = t.attrs["t"].orEmpty()
                    value = null; inline = null
                    if (t.selfClosing) cellCol = -1
                }
                "v" -> if (!t.selfClosing) { value = s.text(); inValue = true }
                "/v" -> inValue = false
                "is" -> inline = StringBuilder()
                "t" -> if (!t.selfClosing) inline?.append(s.text())
                "/c" -> finishCell()
            }
        }
        if (rows.isEmpty()) return emptyList()
        val last = rows.keys.max()
        return (0..last).map { r ->
            val cells = rows[r] ?: return@map emptyList()
            val width = (cells.keys.maxOrNull() ?: -1) + 1
            List(width) { c -> cells[c] ?: SheetCell.EMPTY }
        }
    }

    /** 1.0 → "1", 0.25 → "0.25", 1234567.0 → "1234567" (no exponent for whole numbers). */
    fun formatNumber(d: Double): String {
        if (d == kotlin.math.floor(d) && kotlin.math.abs(d) < 1e15) return d.toLong().toString()
        val r = kotlin.math.round(d * 1e9) / 1e9
        return r.toString()
    }
}

/** CSV / semicolon / tab separated text. UTF-8 (with or without BOM), otherwise Windows-1252. */
object CsvReader {
    private val CP1252_HIGH = charArrayOf(
        '€', '\u0081', '‚', 'ƒ', '„', '…', '†', '‡', 'ˆ', '‰', 'Š', '‹', 'Œ', '\u008D', 'Ž', '\u008F',
        '\u0090', '‘', '’', '“', '”', '•', '–', '—', '˜', '™', 'š', '›', 'œ', '\u009D', 'ž', 'Ÿ'
    )

    fun decode(data: ByteArray): String {
        val bytes = if (data.size >= 3 && data[0] == 0xEF.toByte() && data[1] == 0xBB.toByte() && data[2] == 0xBF.toByte()) data.copyOfRange(3, data.size) else data
        return try {
            bytes.decodeToString(throwOnInvalidSequence = true)
        } catch (e: Exception) {
            buildString(bytes.size) {
                for (b in bytes) {
                    val v = b.toInt() and 0xFF
                    append(if (v in 0x80..0x9F) CP1252_HIGH[v - 0x80] else v.toChar())
                }
            }
        }
    }

    fun read(data: ByteArray, name: String = "CSV"): SheetData {
        val text = decode(data)
        val firstLine = text.lineSequence().firstOrNull { it.isNotBlank() }.orEmpty()
        val sep = listOf(';', '\t', ',').maxByOrNull { c -> firstLine.count { it == c } } ?: ';'
        val rows = mutableListOf<List<SheetCell>>()
        var row = mutableListOf<SheetCell>()
        val field = StringBuilder()
        var quoted = false
        var i = 0
        fun endField() {
            val t = field.toString()
            row += SheetCell(t.trim(), parseNumber(t))
            field.clear()
        }
        while (i < text.length) {
            val c = text[i]
            if (quoted) {
                if (c == '"') {
                    if (i + 1 < text.length && text[i + 1] == '"') { field.append('"'); i++ } else quoted = false
                } else field.append(c)
            } else when (c) {
                '"' -> if (field.isBlank()) { field.clear(); quoted = true } else field.append(c)
                sep -> endField()
                '\r' -> {}
                '\n' -> { endField(); rows += row; row = mutableListOf() }
                else -> field.append(c)
            }
            i++
        }
        if (field.isNotEmpty() || row.isNotEmpty()) { endField(); rows += row }
        return SheetData(name, rows.map { r -> if (r.all { it.isBlank }) emptyList() else r })
    }

    /** "1 234,50" / "1234.5" / "25 %" → number; anything else → null. */
    fun parseNumber(raw: String): Double? {
        var t = raw.trim().replace(" ", "").replace(" ", "")
        if (t.isEmpty()) return null
        val percent = t.endsWith("%")
        t = t.removeSuffix("%").removePrefix("kr").removeSuffix("kr").removeSuffix(",-")
        if (t.count { it == ',' } == 1 && t.count { it == '.' } >= 1 && t.lastIndexOf(',') > t.lastIndexOf('.')) t = t.replace(".", "")
        t = t.replace(',', '.')
        if (t.count { it == '.' } > 1) t = t.replace(Regex("\\.(?=.*\\.)"), "")
        if (!Regex("^-?\\d+(\\.\\d+)?$").matches(t)) return null
        val v = t.toDoubleOrNull() ?: return null
        return if (percent) v / 100.0 else v
    }
}
